/**
 * The Applied lists' model (2.J deliverable 16), pure: a step's batches as rows, and NOW's rows
 * from the clock. The first NOW creates the clock with no advance, so it has no row to undo: the
 * step shows it with Undo disabled and the reason. Refusal texts are 2.L's `refusalText`.
 */
import type { ChangeBatchV1, ClockV1 } from '@codex/contracts';

export { refusalText, UNDONE_TEXT } from '../../undo/core/offers.js';

export const FIRST_NOW_TEXT = "The first NOW can't be undone. Set it again to correct it.";

export interface AppliedRow {
  readonly id: string;
  readonly time: string;
  /** What the row says it wrote. */
  readonly summary: string;
  readonly undone: boolean;
  /** Undo is disabled for this reason; null when it is open. */
  readonly disabledReason: string | null;
  /** For NOW: the clock row version Undo is based on. */
  readonly rv: number | null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** A step's batches of its kind, newest first. */
export function batchRows(batches: readonly ChangeBatchV1[], kind: string): AppliedRow[] {
  return batches
    .filter((b) => b.kind === kind && b.revertsId === null)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
    .map((b) => ({
      id: b.id,
      time: b.createdAt,
      summary: plural(b.rowCount, 'change written', 'changes written'),
      undone: b.undoneById !== null,
      disabledReason: null,
      rv: null,
    }));
}

/** NOW's rows: the clock's `bootstrap` advances, newest first, and the first NOW when this step made it. */
export function nowRows(clock: ClockV1 | null, createdClock: boolean): AppliedRow[] {
  const rows: AppliedRow[] = (clock?.advances ?? [])
    .filter((a) => a.reason === 'bootstrap' && a.kind === 'set')
    .map((a) => ({
      id: a.id,
      time: a.createdAt,
      summary: 'NOW set',
      undone: a.undoneAt !== null,
      disabledReason: null,
      rv: clock?.rv ?? null,
    }));
  if (createdClock && clock !== null) {
    rows.push({
      id: 'first-now',
      time: '',
      summary: 'First NOW set',
      undone: false,
      disabledReason: FIRST_NOW_TEXT,
      rv: null,
    });
  }
  return rows;
}
